// Display formatting helpers for stats-mode charts and tables.
// Goal: turn raw DB column names + raw numbers into something readable.

import { format as formatDateFns, isValid, parseISO } from "date-fns";

import { isNumericCell } from "@stockline/shared/validation/bi/numeric";
import { getWeightUnit } from "@/lib/units";
import { getDateLocale } from "@/lib/timeFormatting";

// Import the i18next singleton directly (not `@/lib/i18n`, which runs the app's
// init side effects) so this low-level formatter stays cheap to import and
// doesn't drag the i18n bootstrap into every consumer's module graph.
import i18n from "i18next";

// Resolve the locale to format numbers/dates under. We key off the active UI
// language (the user's explicit choice, e.g. "en" / "fr" / "es") rather than the
// browser locale, so numbers read consistently with the surrounding text and
// currency: a US user sees "1,234.5", a French user "1 234,5". Before i18n is
// initialized `language` is undefined → return undefined so `Intl` /
// `toLocaleString` fall back to the runtime default instead of a hardcoded "fr".
export function activeLocale(): string | undefined {
  return i18n.language || undefined;
}

const ACRONYMS = new Set([
  "id",
  "sku",
  "url",
  "vat",
  "kg",
  "ml",
  "qty",
  "ttc",
  "ht",
]);

export function prettifyColumn(name: string): string {
  if (!name) return name;
  return name
    .replace(/[_\-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => {
      const lower = word.toLowerCase();
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

// French (and other space-grouped locales) group thousands with a NARROW
// NO-BREAK SPACE (U+202F). Its rendered width is unreliable at the display
// layer: a monospace font inflates it to a full cell (the leading group floats
// off — "2   582,52"), while a proportional font renders it so tight the groups
// fuse ("2582,52"). Normalize it to a regular-width NO-BREAK SPACE (U+00A0) so
// the grouping gap is an even, predictable word-space in any font, and still
// never wraps mid-number inside a tooltip.
const NARROW_NO_BREAK_SPACE = "\u202F";
const NO_BREAK_SPACE = "\u00A0";

function normalizeGroupingSpace(s: string): string {
  return s.replaceAll(NARROW_NO_BREAK_SPACE, NO_BREAK_SPACE);
}

export function formatNumber(
  value: number,
  opts?: { compact?: boolean; fractionDigits?: number },
): string {
  if (!Number.isFinite(value)) return String(value);
  const locale = activeLocale();
  if (opts?.compact && Math.abs(value) >= 1000) {
    return normalizeGroupingSpace(
      new Intl.NumberFormat(locale, {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(value),
    );
  }
  // `fractionDigits` pins BOTH bounds so a column can render every cell at the
  // same precision ("20 991,20", "0,00") — ragged decimals ("41 088,51" over
  // "20 991,2") misalign the decimal points and break vertical scanning.
  return normalizeGroupingSpace(
    value.toLocaleString(locale, {
      minimumFractionDigits: opts?.fractionDigits,
      maximumFractionDigits: opts?.fractionDigits ?? 2,
    }),
  );
}

// Canonical weight unit the BI agent tags on weight columns. The stored values
// are already in the tenant-correct magnitude (no conversion), so we only swap
// the LABEL kg→lbs for English locales, matching the recap page's
// `getWeightUnit()`.
const WEIGHT_UNITS = new Set(["kg", "kgs"]);

// Currency symbols/codes the BI agent may tag a money column with. ALL are
// remapped to the active-language currency (see `activeCurrencySymbol`) so BI
// matches the rest of the app (`lib/currency.ts`): a euro figure for a French
// user, a dollar figure for an English one — language-tied, no per-tenant
// currency setting (for now). Compared lower-cased, so "EUR"/"eur" both match.
const CURRENCY_UNITS = new Set([
  "€",
  "$",
  "£",
  "¥",
  "₣",
  "₤",
  "eur",
  "usd",
  "gbp",
  "chf",
]);

// The currency symbol for the active UI language — mirrors `lib/currency.ts`
// `getCurrencySymbol()` (en → "$", fr/es/other → "€"), but read off the bare
// i18next singleton so this low-level formatter keeps no app-i18n side effects.
function activeCurrencySymbol(): string {
  const lang = (i18n.language || "fr").split("-")[0];
  return lang === "en" ? "$" : "€";
}

// A compound "rate" unit the BI agent tags directly on a per-unit price/rate
// column, e.g. "€/kg", "$/piece". Only the numerator/denominator are split —
// each side is localized independently via the same rules as a bare unit.
const COMPOUND_UNIT_RE = /^(.+?)\/(.+)$/;

function localizeUnitPart(part: string): string {
  const u = part.trim().toLowerCase();
  if (WEIGHT_UNITS.has(u)) return getWeightUnit();
  if (CURRENCY_UNITS.has(u)) return activeCurrencySymbol();
  return part.trim();
}

// Map a stored unit to what the user should SEE: weight → kg/lbs by locale,
// any currency → the active-language currency symbol, everything else (%, …)
// untouched. A compound unit ("€/kg") localizes each side independently.
// `unitPlacement` then decides which side it sits on.
export function displayUnit(unit?: string): string | undefined {
  if (!unit) return unit;
  const u = unit.trim().toLowerCase();
  if (WEIGHT_UNITS.has(u)) return getWeightUnit();
  if (CURRENCY_UNITS.has(u)) return activeCurrencySymbol();
  const compound = COMPOUND_UNIT_RE.exec(unit.trim());
  if (compound) {
    return `${localizeUnitPart(compound[1])}/${localizeUnitPart(compound[2])}`;
  }
  return unit;
}

// A date or ISO datetime cell ("2026-06-15" or "2026-06-15T00:00:00"). BI
// queries surface period buckets (day/month) and order dates as these. Fully
// anchored: a string that merely STARTS with a date (a lot code like
// "2026-06-15-BATCH-A" or a label "2026-06-15 promo") must NOT be treated as a
// date and silently truncated. Seconds, fractional seconds, and a trailing
// zone offset are tolerated so a raw `date_trunc`/timestamp value still matches.
const ISO_DATE_RE =
  /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;

export function isDateCell(value: unknown): value is string {
  return typeof value === "string" && ISO_DATE_RE.test(value.trim());
}

// Parse the date-only portion (first 10 chars) of an ISO date/period cell. No
// timezone conversion: these are calendar buckets, so we never shift across a
// day boundary.
function parseDateOnly(value: string): Date | null {
  const parsed = parseISO(value.trim().slice(0, 10));
  return isValid(parsed) ? parsed : null;
}

// The granularity a date column was bucketed at. `date_trunc('year')` yields
// Jan-1 dates, `date_trunc('month')` yields first-of-month dates, and anything
// with real day precision is "day".
export type DateGranularity = "year" | "month" | "day";

// Decide the coarsest granularity that losslessly describes EVERY date bucket in
// a column, so a monthly trend shows "juin 2026" / "Jun 2026" rather than a wall
// of "1 juin 2026" first-of-month labels. We downgrade to "day" the moment any
// value carries real day precision, so genuine daily series keep their full
// date. Columns with no date cells return "day" (the result is then unused).
export function detectDateGranularity(
  values: ReadonlyArray<unknown>,
): DateGranularity {
  let sawDate = false;
  let allFirstOfMonth = true;
  let allFirstOfYear = true;
  for (const v of values) {
    if (!isDateCell(v)) continue;
    const d = parseDateOnly(v);
    if (!d) continue;
    sawDate = true;
    if (d.getDate() !== 1) {
      allFirstOfMonth = false;
      allFirstOfYear = false;
      break;
    }
    if (d.getMonth() !== 0) allFirstOfYear = false;
  }
  if (!sawDate) return "day";
  if (allFirstOfYear) return "year";
  if (allFirstOfMonth) return "month";
  return "day";
}

// Reformat a date/period label to the active locale — date-only, NO timezone
// conversion. The `granularity` picks the precision: a month bucket renders
// "juin 2026" / "Jun 2026", a year bucket "2026", and a day "15 juin 2026" /
// "Jun 15, 2026". Defaults to day precision for standalone (per-cell) callers.
export function formatDateCell(
  value: string,
  granularity: DateGranularity = "day",
): string {
  const parsed = parseDateOnly(value);
  if (!parsed) return value;
  const lang = (i18n.language || "fr").split("-")[0];
  const pattern =
    granularity === "year"
      ? "yyyy"
      : granularity === "month"
        ? "MMM yyyy"
        : lang === "en"
          ? "MMM d, yyyy"
          : "d MMM yyyy";
  return formatDateFns(parsed, pattern, {
    locale: getDateLocale(i18n.language),
  });
}

// Like `formatDateCell` at day precision but with the weekday spelled out —
// "lundi 15 juil. 2026" / "Monday, Jul 15, 2026". Lower-cased in French so it
// reads mid-sentence ("…comparent hier lundi 15 juil. 2026…"). Used by the
// text-widget `{{col:date}}` placeholder to name a specific day inline, where
// the weekday is the point (the year-over-year comparison aligns on it). Same
// no-timezone, date-only parse as the other date formatters here.
export function formatDateWithWeekday(value: string): string {
  const parsed = parseDateOnly(value);
  if (!parsed) return value;
  const lang = (i18n.language || "fr").split("-")[0];
  const pattern = lang === "en" ? "EEEE, MMM d, yyyy" : "EEEE d MMM yyyy";
  return formatDateFns(parsed, pattern, {
    locale: getDateLocale(i18n.language),
  });
}

// Category-axis / label formatter shared by the chart renderers: localize date
// buckets, otherwise stringify. A null/undefined cell becomes "" (recharts'
// existing fallback for a missing category). Per-cell, so date buckets render at
// day precision; charts that have the whole column should use
// `makeCategoryFormatter` instead so month/year buckets read correctly.
export function formatCategory(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (isDateCell(value)) return formatDateCell(value);
  return String(value);
}

// Build a category formatter bound to a column's detected date granularity, so
// every label in a `date_trunc('month'|'year')` series renders at the same
// coarse precision ("juin 2026", "2026") instead of day-level first-of-month
// noise. Pass the full column of category cells (e.g. `rows.map((r) => r[xIdx])`).
export function makeCategoryFormatter(
  values: ReadonlyArray<unknown>,
): (value: unknown) => string {
  const granularity = detectDateGranularity(values);
  return (value: unknown) => {
    if (value === null || value === undefined) return "";
    if (isDateCell(value)) return formatDateCell(value, granularity);
    return String(value);
  };
}

// Units that read as a SUFFIX by universal convention ("42%", not "%42").
// Everything else (currency symbols €/$/£, kg, …) keeps the prefix placement
// that matches the single_value KPI card.
const SUFFIX_UNITS = new Set(["%"]);

// Currency placement is driven by the SYMBOL, not the UI language — a euro
// figure must read "1 234 €" for the business that uses it, never the
// dollar-style "€1 234", even when the app is in English. Symbols that sit
// BEFORE the amount ("$1,234", "£5", "¥100"):
const PREFIX_CURRENCY_SYMBOLS = new Set(["$", "£", "¥", "₤"]);
// …and the ones that sit AFTER it ("1 234 €", "1 234 CHF"):
const SUFFIX_CURRENCY_SYMBOLS = new Set(["€", "₣", "CHF", "Fr"]);

// Where a unit sits relative to the number. `%` and euro-style currencies are
// suffixes; dollar-style currencies are prefixes; any other tagged unit (kg, …)
// keeps the prefix placement the single_value KPI card uses. A compound unit
// ("€/kg", "$/piece") follows its currency numerator's own placement, since it
// reads as a price. Shared by `formatValue` and the KPI card so a chart
// tooltip and its big-number widget never disagree.
//
// NOTE: a table no longer consults this at all — a column's unit is stated
// once in its header and the cells are bare numbers (STK-1672). The only
// callers left are the chart renderers (axis ticks + tooltips) and the KPI
// card, so this stays as they had it.
export function unitPlacement(unit: string): "prefix" | "suffix" {
  if (SUFFIX_UNITS.has(unit)) return "suffix";
  if (SUFFIX_CURRENCY_SYMBOLS.has(unit)) return "suffix";
  if (PREFIX_CURRENCY_SYMBOLS.has(unit)) return "prefix";
  const compound = COMPOUND_UNIT_RE.exec(unit);
  if (compound) {
    const numerator = compound[1].trim();
    if (SUFFIX_CURRENCY_SYMBOLS.has(numerator)) return "suffix";
    if (PREFIX_CURRENCY_SYMBOLS.has(numerator)) return "prefix";
  }
  return "prefix";
}

// Format a numeric value with an optional unit. Placement is locale-aware via
// `unitPlacement` (prefix "$1.2K" for English currency, suffix "1,2 k €" for
// French; "42%" always suffixed). A suffixed currency gets a non-breaking space
// before it; "%" stays glued. Used by the chart renderers for Y-axis ticks
// (compact) and tooltips (full precision). No unit → the bare number.
export function formatValue(
  value: number,
  unit?: string,
  opts?: { compact?: boolean; fractionDigits?: number },
): string {
  const formatted = formatNumber(value, opts);
  const u = displayUnit(unit);
  if (!u) return formatted;
  if (unitPlacement(u) === "prefix") {
    // Only a bare dollar-style symbol glues tight to the number by
    // convention ("$1,234"). Any other prefix unit (kg, items, a compound
    // "$/kg" rate) needs a gap — otherwise it visually fuses with the first
    // digit group while the locale's own thousands-separator space between
    // digit groups reads as a much bigger gap ("kg27 687": "27" glued to the
    // unit, far from "687").
    const gap = PREFIX_CURRENCY_SYMBOLS.has(u) ? "" : " ";
    return `${u}${gap}${formatted}`;
  }
  // Glue "%" to the number; separate a trailing currency with a thin space.
  const gap = u === "%" ? "" : " ";
  return `${formatted}${gap}${u}`;
}

// Direction of a difference/variation value: +1 rise, -1 drop, 0 no change
// (a null/non-finite delta reads as "no change" — no arrow, muted). Drives the
// colour + arrow a `delta`-role table column renders (STK-808).
export function deltaSign(value: number | null): -1 | 0 | 1 {
  if (value === null || !Number.isFinite(value) || value === 0) return 0;
  return value > 0 ? 1 : -1;
}

// Format a difference/variation with an explicit sign so a rise/drop reads at a
// glance ("+12.4 %", "-1 234 €"). The magnitude is unit-formatted exactly like
// `formatValue`; only the leading sign is added (zero stays unsigned). Used by
// the `delta`-role table column alongside `deltaSign` for the colour + arrow.
export function formatDelta(
  value: number,
  unit?: string,
  opts?: { fractionDigits?: number },
): string {
  // A non-finite delta reads as "no change" (matching `deltaSign`); render a
  // stable neutral label rather than "NaN"/"Infinity".
  if (!Number.isFinite(value)) return "—";
  const magnitude = formatValue(Math.abs(value), unit, opts);
  if (value > 0) return `+${magnitude}`;
  if (value < 0) return `-${magnitude}`;
  return magnitude;
}

// The signed percentage change of `current` against a `comparison` baseline —
// `((current − comparison) / comparison) × 100`. `null` means "no meaningful
// comparison": a non-finite `current` (NaN/Infinity from a corrupt cell), a null
// baseline, or a baseline of 0 (dividing by it is undefined), so the caller
// shows a neutral placeholder ("—") instead of `NaN%`/`Infinity%`. Shared by the
// comparison-bar pill and the single_value KPI card so every widget computes a
// %Δ the same way (a negative baseline keeps its sign — we divide by the signed
// baseline, not its magnitude).
export function computeCategoryProgress(
  current: number,
  comparison: number | null,
): number | null {
  if (comparison === null || comparison === 0 || !Number.isFinite(current))
    return null;
  return ((current - comparison) / comparison) * 100;
}

// Tolerate a small fraction of non-numeric cells (e.g. "n/a", trailing total
// rows) before treating a column as non-numeric.
const NUMERIC_COLUMN_THRESHOLD = 0.8;

export function isNumericColumn(values: ReadonlyArray<unknown>): boolean {
  let seen = 0;
  let numeric = 0;
  for (const v of values) {
    if (v === null || v === undefined) continue;
    seen += 1;
    // Use the shared numeric-cell predicate so a pg numeric string ("123.4")
    // counts as numeric here exactly as it does for the validator + renderers.
    if (isNumericCell(v)) numeric += 1;
  }
  return seen > 0 && numeric / seen >= NUMERIC_COLUMN_THRESHOLD;
}
